import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import type {
  MapProvider,
  MapProviderType,
  LatLngLiteral,
  GeocodingResult,
  AutocompleteResult,
} from 'src/types/map-provider';
import { MapProviderError } from 'src/types/map-provider';
import type { UseMapProviderOptions, UseMapProviderReturn } from 'src/types/map-hooks';
import { createMapProvider } from 'src/utils/map-provider-factory';
import { useMapProviderSelection } from './use-map-provider-selection';
import { useEnvMapApiKeys } from './use-env-map-provider';

/**
 * Hook for loading and managing map providers
 *
 * Handles provider type determination, loading with caching, and exposes geocoding APIs.
 * Supports both parameter-based and environment-based API keys with parameter priority.
 *
 * @example
 * ```tsx
 * const { provider, loadProvider, geocodeAddress } = useMapProvider({
 *   providerType: 'google',
 *   apiKeys: { google: 'YOUR_API_KEY' },
 *   autoLoad: true,
 * });
 * ```
 *
 * @param options - Configuration options
 * @param options.providerType - Provider to use ('google' | 'baidu' | 'openstreetmap' | 'auto')
 * @param options.apiKeys - API keys for providers (falls back to env vars)
 * @param options.autoLoad - Auto-load provider on mount (default: true)
 * @param options.onProviderChange - Callback when provider changes
 * @param options.onError - Callback for error handling
 *
 * @returns Provider state and geocoding APIs
 */
export function useMapProvider(options: UseMapProviderOptions = {}): UseMapProviderReturn {
  const {
    providerType = 'auto',
    apiKeys = {},
    autoLoad = true,
    onProviderChange,
    onError,
  } = options;

  // State
  const [provider, setProvider] = useState<MapProvider | null>(null);
  const [currentProviderType, setCurrentProviderType] = useState<MapProviderType | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<MapProviderError | null>(null);

  // Refs
  const providersRef = useRef(new Map<MapProviderType, MapProvider>());
  const mountedRef = useRef(true);

  // Reuse existing hooks for provider selection and API keys
  const { getProviderType } = useMapProviderSelection();
  const envApiKeys = useEnvMapApiKeys();

  // Merge API keys: environment vars + parameters (parameters take priority)
  const effectiveApiKeys = useMemo(
    () => ({
      ...envApiKeys,
      ...apiKeys,
    }),
    [envApiKeys, apiKeys]
  );

  // Determine provider type (handles 'auto' with async detection)
  const determineProviderType = useCallback(async (): Promise<MapProviderType> => {
    if (providerType !== 'auto') {
      return providerType;
    }
    return await getProviderType();
  }, [providerType, getProviderType]);

  // Load provider with caching
  const loadProvider = useCallback(
    async (type?: MapProviderType) => {
      if (isLoading) return;

      setIsLoading(true);
      setError(null);

      try {
        const targetType = type || (await determineProviderType());

        // Return early if already loaded
        if (currentProviderType === targetType && provider) {
          setIsLoading(false);
          return;
        }

        // Check hook-level cache first
        let providerInstance = providersRef.current.get(targetType);

        if (!providerInstance) {
          // Falls back to global factory cache
          const apiKey = effectiveApiKeys[targetType];
          providerInstance = await createMapProvider(targetType, apiKey);
          providersRef.current.set(targetType, providerInstance);
        }

        if (!mountedRef.current) return;

        setProvider(providerInstance);
        setCurrentProviderType(targetType);
        onProviderChange?.(providerInstance, targetType);
      } catch (err) {
        const providerError =
          err instanceof MapProviderError
            ? err
            : new MapProviderError(
                `Failed to load map provider: ${err instanceof Error ? err.message : String(err)}`
              );

        if (!mountedRef.current) return;

        setError(providerError);
        onError?.(providerError);
      } finally {
        if (mountedRef.current) {
          setIsLoading(false);
        }
      }
    },
    [
      determineProviderType,
      effectiveApiKeys,
      currentProviderType,
      provider,
      isLoading,
      onProviderChange,
      onError,
    ]
  );

  // Geocoding APIs
  const geocodeAddress = useCallback(
    async (address: string): Promise<GeocodingResult | null> => {
      if (!provider) return null;
      try {
        return await provider.geocodeAddress(address);
      } catch (err) {
        console.error('Geocoding error:', err);
        return null;
      }
    },
    [provider]
  );

  const reverseGeocode = useCallback(
    async (position: LatLngLiteral): Promise<GeocodingResult | null> => {
      if (!provider) return null;
      try {
        return await provider.reverseGeocode(position);
      } catch (err) {
        console.error('Reverse geocoding error:', err);
        return null;
      }
    },
    [provider]
  );

  const getPlacePredictions = useCallback(
    async (input: string): Promise<AutocompleteResult[]> => {
      if (!provider) return [];
      try {
        return await provider.getPlacePredictions(input);
      } catch (err) {
        console.error('Get place predictions error:', err);
        return [];
      }
    },
    [provider]
  );

  const getPlaceDetails = useCallback(
    async (placeId: string): Promise<AutocompleteResult | null> => {
      if (!provider) return null;
      try {
        return await provider.getPlaceDetails(placeId);
      } catch (err) {
        console.error('Get place details error:', err);
        return null;
      }
    },
    [provider]
  );

  // Cleanup
  const cleanup = useCallback(() => {
    // Destroy all cached providers
    providersRef.current.forEach((p) => p.destroy());
    providersRef.current.clear();

    setProvider(null);
    setCurrentProviderType(null);
    setError(null);
  }, []);

  // Auto-load on mount
  useEffect(() => {
    if (autoLoad && !provider && !isLoading) {
      loadProvider();
    }
  }, [autoLoad]); // Intentionally minimal deps to run only once

  // Lifecycle management
  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      cleanup();
    };
  }, [cleanup]);

  // Return memoized value (follows useBoolean pattern)
  const memoizedValue = useMemo<UseMapProviderReturn>(
    () => ({
      provider,
      providerType: currentProviderType,
      isLoading,
      isScriptLoaded: provider?.isScriptLoaded() ?? false,
      error,
      loadProvider,
      geocodeAddress,
      reverseGeocode,
      getPlacePredictions,
      getPlaceDetails,
      cleanup,
    }),
    [
      provider,
      currentProviderType,
      isLoading,
      error,
      loadProvider,
      geocodeAddress,
      reverseGeocode,
      getPlacePredictions,
      getPlaceDetails,
      cleanup,
    ]
  );

  return memoizedValue;
}
